import { Heart, Award, MapPin } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/Card';
import { Button } from '../components/ui/Button';
import { Link } from 'react-router-dom';
import founderSusan from '../assets/founder-susan.webp';
import lighthouse from '../assets/lighthouse.webp';

export function AboutPage() {
  return (
    <div>
      <section className="bg-gradient-to-b from-sky-100 via-sky-50 to-white">
        <div className="py-20">
          <div className="container-max max-w-3xl mx-auto">
            <h2 className="text-3xl md:text-4xl font-serif font-bold mb-10 text-center text-coastal-700">
              Our Story
            </h2>
            <div className="font-script text-2xl md:text-3xl text-gray-800 leading-relaxed">
              <figure className="mb-6 w-40 sm:w-44 md:w-52 mx-auto sm:mx-0 sm:float-right sm:ml-7 sm:mb-3">
                <img
                  src={lighthouse}
                  alt="The Fire Island Lighthouse rising above the dune grass on Long Island at dusk"
                  title="Fire Island Lighthouse · Long Island"
                  width={1200}
                  height={1600}
                  loading="lazy"
                  decoding="async"
                  className="w-full aspect-[3/4] object-cover rounded-2xl shadow-md ring-1 ring-black/5"
                />
              </figure>
              <p className="mb-6">
                As a pharmacist for over 20 years, I have seen firsthand the important role
                nutrition can play in our overall wellness. Like many people, I didn&apos;t always
                apply that knowledge to my own daily life. That changed a few years ago, when a
                personal health challenge inspired me to take a closer look at the foods I was
                eating and adopt a simpler, more mindful approach to food.
              </p>
              <p className="mb-6">
                I eat yogurt almost daily and love adding a good crunch to it, but had a hard time
                finding products made with the clean, simple ingredients I was looking for, so I
                started making my own. Now I simply enjoy it with my morning coffee.
              </p>
              <p className="mb-6">
                What began in my kitchen on Long Island became Coastal Harvest Co. Every batch is
                thoughtfully crafted, made with ingredients I feel good about and love sharing
                with family and friends.
              </p>
              <p>
                This was created as part of my own wellness journey, and now I&apos;m proud to share
                it with you. I hope you enjoy it as much as we do.
              </p>
            </div>

            <figure className="clear-both mt-12 flex flex-col items-center text-center">
              <img
                src={founderSusan}
                alt="Susan, founder of Coastal Harvest Co., sitting on a driftwood log on a Long Island beach at sunset"
                width={640}
                height={800}
                loading="lazy"
                decoding="async"
                className="w-44 sm:w-52 md:w-60 aspect-[4/5] object-cover rounded-2xl shadow-lg ring-1 ring-black/5"
              />
              <figcaption className="mt-5">
                <p className="text-2xl md:text-3xl font-serif text-coastal-600">&mdash; Susan</p>
                <p className="mt-1 text-sm uppercase tracking-wide text-gray-500">
                  Founder, Coastal Harvest Co.
                </p>
              </figcaption>
            </figure>
          </div>
        </div>
      </section>

      <section className="py-16 bg-cream">
        <div className="container-max">
          <h2 className="text-3xl font-serif font-bold text-center text-coastal-600 mb-12">Our Values</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <Card>
              <CardHeader>
                <Heart className="w-12 h-12 text-teal-400 mb-4" />
                <CardTitle>Wellness First</CardTitle>
              </CardHeader>
              <CardContent>
                Every decision is guided by what nourishes the body. No refined sugars, no unnecessary additives.
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <Award className="w-12 h-12 text-teal-400 mb-4" />
                <CardTitle>Pharmacist Crafted</CardTitle>
              </CardHeader>
              <CardContent>
                Blending science with simplicity. Each recipe is developed with an understanding of how ingredients impact the body.
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <MapPin className="w-12 h-12 text-teal-400 mb-4" />
                <CardTitle>Rooted Locally</CardTitle>
              </CardHeader>
              <CardContent>
                Made on Long Island and popping up at local spots around the Island —{' '}
                <Link to="/locations" className="font-semibold text-coastal-700 underline hover:text-coastal-900">
                  see where to find us
                </Link>.
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      <section className="bg-coastal-600 text-white py-16">
        <div className="container-max text-center">
          <h2 className="text-3xl font-bold mb-4">Experience Clean Nutrition</h2>
          <p className="text-lg mb-8 max-w-xl mx-auto opacity-90">
            Thoughtfully made foods rooted in wellness and inspired by the coast.
          </p>
          <Link to="/products">
            <Button variant="secondary" size="lg">View Our Products</Button>
          </Link>
        </div>
      </section>
    </div>
  );
}
